<template>
  <div
    :id="carouselId"
    ref="productCarousel"
    class="product-carousel"
    :class="[
      { 'is-loading': state !== 'ready' },
      { 'is-wide': isWideCarousel }
    ]"
    js-product-carousel
  >
    <ul
      ref="carouselContainer"
      class="product-carousel__list"
      @scroll.passive="handleCarouselScroll"
    >
      <li
        v-for="(product, index) in products"
        ref="slides"
        :key="product.key"
        class="product-carousel__list-item"
        :style="getSkeletonAnimationDelay(index)"
        :js-carousel-slide="index"
      >
        <product-card-link
          v-if="showSimplifiedProductCard"
          :key="`${product.key}-link`"
          :initial-product="product"
          :tracking-parameters="trackingParameters"
          :context="productCardContext"
        />

        <product-card
          v-else
          :key="product.key"
          :grid-active-variant-selector="activeVariantSelector"
          :initial-product="product"
          :context="productCardContext"
          :is-cart-popup="isCartPopup"
          :tracking-parameters="trackingParameters"
          :use-packshot="productCardUsePackshot"
          :aspect-ratio="aspectRatio"
          :prevent-drawer="preventDrawer"
          :hide-wishlist-icon="hideWishlistIcon"
          :hide-product-swatches="hideProductSwatches"
          @variant-selector-toggled="handleVariantSelectorToggle"
        />
      </li>
    </ul>

    <nav
      v-if="showArrows"
      class="product-carousel__arrows"
    >
      <button
        class="product-carousel__arrow-button product-carousel__arrow-button-prev ico-button"
        type="button"
        :aria-label="carouselPrevLabel"
        :disabled="disablePrevButton"
        :tabindex="-1"
        @click.prevent="handleArrowClick('prev')"
      >
        <icon-right />
      </button>
      <button
        class="product-carousel__arrow-button product-carousel__arrow-button-next ico-button"
        type="button"
        :aria-label="carouselNextLabel"
        :disabled="disableNextButton"
        :tabindex="-1"
        @click.prevent="handleArrowClick('next')"
      >
        <icon-right />
      </button>
    </nav>
  </div>
</template>

<script>

/**
 * Component: Product Carousel
 * ------------------------------------------------------------------------------
 * - Product Card Carousel.
 * @param {Array} products - Array of formatted product objects.
 * @param {Object} [trackingParameters] - Object of parameters, typically added to events (Bloomreach, Attraqt, Google).
 * @param {String} [sectionId] - Unique section ID, used for Swym Wishlist initialisation.
 * @param {Boolean} isCartPopup - Defines if component is used inside the cart popup.
 * @param {String} context - Location of the rendered carousel.
 * @param {Boolean} [productCardUsePackshot] - If to use Packshots as Primary Product Card images.
 * @param {Boolean|Object} [slidesPerView] - An object with number of slides to display per `mobile`, `tablet`, `desktop` and `wide` (optional) breakpoints.
 * @param {Boolean} [preventDrawer] - Prevent cart drawer from opening when product added to the cart (overlay will be shown instead).
 * @param {Boolean} [hideWishlistIcon] - Whether to hide the wishlist icon from product cards.
 * @param {Boolean} [hideProductSwatches] - Whether to hide the colour swatches icon from product cards.
 * @param {String|Number} [aspectRatio] - Force / override Product Card media aspect ratio.
 */
import { mapState } from 'pinia'
import { useScreenStore } from '~stores/screen'

import { useWishlistStore } from '~stores/wishlist'

import ProductCard from '~components/product-card/product-card'
import ProductCardLink from '~components/product-card-link/product-card-link'

import IconRight from '~icons/direction/right.svg'

export default {
  name: 'ProductCarousel',

  components: {
    IconRight,
    ProductCard,
    ProductCardLink,
  },

  props: {
    aspectRatio: {
      type: [String, Number],
      default: 0.728,
    },
    products: {
      type: Array,
      default: () => [],
    },
    productCardUsePackshot: {
      type: Boolean,
      default: false,
    },
    trackingParameters: {
      type: Object,
      default: () => ({}),
    },
    sectionId: {
      type: String,
      default: '',
    },
    isCartPopup: {
      type: Boolean,
      default: false,
    },
    slidesPerView: {
      type: [Boolean, Object],
      default: false,
    },
    context: {
      type: String,
      default: 'default',
    },
    preventDrawer: {
      type: Boolean,
      default: false,
    },
    hideWishlistIcon: {
      type: Boolean,
      default: false,
    },
    hideProductSwatches: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['variant-selector-toggled'],

  data() {
    return {
      activeSlideIndex: 0,
      activeVariantSelector: false,
      slideStates: {
        firstSlideVisible: true,
        lastSlideVisible: false,
      },
      productCount: 0,
      state: 'loading',
    }
  },

  computed: {

    /**
     * Map global state.
     */
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
    }),

    /**
     * Map Wishlist state.
     */
    ...mapState(useWishlistStore, ['wishlistReady']),

    /**
     * Calculate a unique ID for the element.
     * @returns {String}
     */
    carouselId() {
      return `ProductCarousel-${this.sectionId}`
    },

    /**
     * Returns an accessible label for the carousels 'Next' button.
     * @returns {String}
     */
    carouselNextLabel() {
      return mint.strings.accessibility.carousel.next
    },

    /**
     * Returns an accessible label for the carousels 'Prev' button.
     * @returns {String}
     */
    carouselPrevLabel() {
      return mint.strings.accessibility.carousel.prev
    },

    /**
     * Calculate if to disable the 'Previous' button.
     * @returns {Boolean}
     */
    disableNextButton() {
      if (this.state !== 'ready') {
        return true
      }

      return this.slideStates.lastSlideVisible
    },

    /**
     * Calculate if to disable the 'Previous' button.
     * @returns {Boolean}
     */
    disablePrevButton() {
      if (this.state !== 'ready') {
        return true
      }

      return this.slideStates.firstSlideVisible
    },

    /**
     * Calculate whether carousel is wide / full width, depending on `slidesPerView`.
     * - If a `wide` breakpoint is specified than the carousel is most likely
     * used in a full-size container.
     * - In this case we add an additional class so that skeletons are loaded
     * correctly at such resolutions
     * @returns {Boolean}
     */
    isWideCarousel() {
      return Boolean(typeof this.slidesPerView?.wide === 'number')
    },

    /**
     * Calculate if the element should be scrollable.
     * - 2.5 visible on mobile, but design has no arrows displaying.
     * - so check for > `slidesPerView.desktop` (4 by default) products
     * @returns {Boolean}
     */
    showArrows() {
      if (this.isMobile) {
        return false
      }

      if (this.isCartPopup) {
        return true
      }

      const targetCardCount = this.slidesPerView?.desktop || 4

      return (this.productCount > targetCardCount)
    },

    /**
     * Calculate the context of product card.
     * @returns {String}
     */
    productCardContext() {
      return this.isCartPopup ? 'cart-popup' : 'default'
    },

    /**
     * Calculate whether to show the `product-card-link` or the full `product-card`
     * component.
     *
     * @returns {Boolean}
     */
    showSimplifiedProductCard() {
      return this.isCartPopup || this.context === 'back-in-stock'
    },
  },

  watch: {

    /**
     * Watch 'Product' data
     * - Initial prop passed through will be an array of 4 empty products
     * - This is to display the skeletons
     * - Check every product is a valid 'product' object.
     * - This assumes that once product data is set, it won't change.
     */
    products: {
      handler() {
        if (this.state === 'ready') {
          return
        }

        const ready = this.products.every((product) => {
          return typeof product.handle === 'string'
        })

        this.state = ready ? 'ready' : 'loading'

        this.setCardCount()
      },
      deep: true,
    },
  },

  methods: {

    /**
     * Set the 'Card Count' CSS variable.
     * - This ensures the CSS grid alignment is correct.
     */
    setCardCount() {

      /**
       * `$refs` don't consistently work if the carousel is rendered under `v-if`
       * conditions
       */
      const productCarousel = this.$refs.productCarousel || document.getElementById(this.carouselId)
      this.productCount = this.products.length

      if (!productCarousel) {
        return
      }

      productCarousel.style.setProperty(
        '--card-count',
        this.productCount,
      )

      if (!this.slidesPerView) {
        return
      }

      const {
        mobile = 1.25,
        tablet = 2.22,
        desktop = 3,
        xlarge = 3,
        wide = false,
      } = this.slidesPerView

      productCarousel.style.setProperty(
        '--card-width-mobile',
        `${(100 / mobile).toFixed(2)}%`,
      )

      productCarousel.style.setProperty(
        '--card-width-tablet',
        `${(100 / tablet).toFixed(2)}%`,
      )

      productCarousel.style.setProperty(
        '--card-width-desktop',
        `${(100 / desktop).toFixed(2)}%`,
      )

      productCarousel.style.setProperty(
        '--card-width-xlarge',
        `${(100 / xlarge).toFixed(2)}%`,
      )

      if (this.isWideCarousel) {
        productCarousel.style.setProperty(
          '--card-width-wide',
          `${(100 / wide).toFixed(2)}%`,
        )
      }
    },

    /**
     * Handle carousel arrow press.
     * - On 'Prev', scroll to the previous slide.
     * - On 'Next', scroll to the next slide.
     * @param {String} direction - Direction of arrow. Prev/Next.
     */
    handleArrowClick(direction = 'next') {
      const container = this.$refs.carouselContainer

      // Note: querySelectorAll in Vue is an anti-pattern, but this.$refs was inconsistent.
      const slides = [...container.querySelectorAll('[js-carousel-slide]')]
      const containerScrollLeft = container.scrollLeft

      // Calculate the width of one slide (assuming all slides are the same width)
      const slideWidth = slides[0]?.clientWidth || 0

      const scrollAmount = direction === 'next' ? slideWidth : -slideWidth
      const newScrollLeft = containerScrollLeft + scrollAmount

      // Scroll to the new position
      container.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth',
      })
    },

    /**
     * Handle events for when the user has manually scrolled the carousel.
     * @param {Event} event - Scroll event
     */
    handleCarouselScroll(event) {
      const scrollLeft = event.target.scrollLeft

      this.slideStates.firstSlideVisible = Boolean(scrollLeft === 0)

      this.slideStates.lastSlideVisible = Boolean(
        event.target.scrollLeft + event.target.offsetWidth === event.target.scrollWidth,
      )
    },

    /**
     * Return a valid CSS variable to define animation delay.
     * @param {Number} index - Array index.
     * @returns {String}
     */
    getSkeletonAnimationDelay(index = 0) {
      if (this.state === 'ready') {
        return false
      }

      return `--skeleton-animation-delay: ${index * 0.1}s`
    },

    /**
     * Updates the handle of the current open Variant Selector.
     * - This variable is updated from an event inside the Product Card, running
     * whenever a variant selector is toggled.
     * - The handle is passed down to Product Cards
     * - This ensures only one selector is open at a given time.
     * - Emits `variant-selector-toggled` event upward from an product-card
     * component.
     * - The event is re-emitted to allow parent components to respond to
     * overlay changes.
     * @param {Object} payload - Emitted payload object.
     * @param {String} payload.handle - The handle of the product.
     * @param {String} payload.state - State of the variant selector (`visible`/`hidden`).
     *
     * @event variant-selector-toggled
     */
    handleVariantSelectorToggle(payload) {
      const { handle = false, state = 'hidden' } = payload || {}

      if (state === 'visible') {
        this.activeVariantSelector = handle
      }

      this.$emit('variant-selector-toggled', payload)
    },
  },
}
</script>
